import { FC, useCallback, useMemo } from 'react';

import { useUIContext } from 'state/ui';

import { ModifierListItem } from './modifier-list-item';
import { IModifierMultiplier, IModifierTypeUIProps } from './types';
import {
  getMultiplierLabel,
  getSelectedMultiplier,
  isMultiplierVisuallySelected,
  logProductModifierCustomization,
} from './utils';

export const ModifierNonNumeric: FC<IModifierTypeUIProps> = ({
  modifier,
  selections,
  onSelectionsChange,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const selectedMultiplier = getSelectedMultiplier(modifier, selections);
  const selectedMultiplierIsZeroMultiplier = selectedMultiplier.multiplier === 0;
  const isSelected = isMultiplierVisuallySelected(selectedMultiplier);

  const allOptionsHavePrefix = modifier.multipliers.every(multiplier => !!multiplier.prefix);

  const badgeLabel = getMultiplierLabel(allOptionsHavePrefix, selectedMultiplier);

  const radioListOptions = useMemo(() => {
    return modifier.multipliers.map(multiplier => {
      const label = getMultiplierLabel(allOptionsHavePrefix, multiplier);
      const price = multiplier.price ?? 0;
      return {
        label: label || '',
        labelSuffix: price > 0 ? `+${formatCurrencyForLocale(price)}` : '',
        value: multiplier.id,
      };
    });
  }, [allOptionsHavePrefix, formatCurrencyForLocale, modifier]);

  const onOptionPress = useCallback(
    (multiplier: IModifierMultiplier, optionValue: string) => {
      onSelectionsChange({ [optionValue]: 1 });
      logProductModifierCustomization(multiplier.name, modifier.name, optionValue);
    },
    [onSelectionsChange, modifier]
  );

  return (
    <ModifierListItem
      isSelected={isSelected}
      badgeContent={!selectedMultiplierIsZeroMultiplier ? badgeLabel : ''}
      content={modifier}
      selectedOption={selectedMultiplier.id}
      itemValue={selectedMultiplier}
      options={radioListOptions}
      onOptionPress={onOptionPress}
    />
  );
};
